import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import AuthForm from '../Auth/AuthForm';
import SafeIcon from '../../common/SafeIcon';

const CorporateContext = createContext(null);
export const useCorporateClient = () => useContext(CorporateContext);

export default function CorporateGate({ children }) {
  const { slug } = useParams();
  const [status, setStatus] = useState('loading'); // loading | unauthenticated | authorized | denied | not_found
  const [corporateClient, setCorporateClient] = useState(null);
  const [session, setSession] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function checkAccess(sess) {
      if (!sess) {
        if (mounted) setStatus('unauthenticated');
        return;
      }

      // Look up the company by slug
      const { data: client, error: clientError } = await supabase
        .from('corporate_clients')
        .select('*')
        .eq('slug', slug)
        .eq('active', true)
        .single();

      if (clientError || !client) {
        if (mounted) setStatus('not_found');
        return;
      }

      // Check the logged-in user's profile is linked to this company
      const { data: profile } = await supabase
        .from('profiles')
        .select('corporate_client_id, is_admin')
        .eq('id', sess.user.id)
        .single();

      if (!profile) {
        if (mounted) setStatus('denied');
        return;
      }

      // Admins can access any corporate page (useful for testing)
      const isAdmin = profile.is_admin === true;
      const isCoordinator = profile.corporate_client_id === client.id;

      if (!isAdmin && !isCoordinator) {
        if (mounted) setStatus('denied');
        return;
      }

      if (mounted) {
        setCorporateClient(client);
        setStatus('authorized');
      }
    }

    supabase.auth.getSession().then(({ data: { session: sess } }) => {
      setSession(sess);
      checkAccess(sess);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (!mounted) return;
      setSession(sess);
      setStatus('loading');
      checkAccess(sess);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [slug]);

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blush-300" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 bg-warmwhite gap-6">
        <div className="text-center space-y-2">
          <p className="text-xs font-bold text-sage-400 uppercase tracking-widest">Corporate Ordering</p>
          <h2 className="text-3xl font-serif text-charcoal">Sign in to place your order</h2>
          <p className="text-sage-500 text-sm">Use the account credentials your company administrator set up for you.</p>
        </div>
        <AuthForm initialMode="login" allowSignup={false} />
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 bg-warmwhite text-center">
        <div className="glass-panel p-10 max-w-md space-y-4">
          <div className="w-16 h-16 bg-sage-50 rounded-full flex items-center justify-center mx-auto">
            <SafeIcon name="MapPin" className="text-3xl text-sage-400" />
          </div>
          <h2 className="text-2xl font-serif text-charcoal">Page not found</h2>
          <p className="text-sage-500">This company ordering page doesn't exist or is no longer active.</p>
        </div>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 bg-warmwhite text-center">
        <div className="glass-panel p-10 max-w-md space-y-6">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
            <SafeIcon name="AlertCircle" className="text-3xl text-red-400" />
          </div>
          <h2 className="text-2xl font-serif text-charcoal">Not authorized</h2>
          <p className="text-sage-500">Your account is not associated with this company's ordering page.</p>
          <button
            onClick={() => supabase.auth.signOut()}
            className="btn-secondary w-full"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <CorporateContext.Provider value={{ corporateClient, session }}>
      {children}
    </CorporateContext.Provider>
  );
}
